import React, { useRef, useState, useCallback, useEffect } from 'react';
import { history } from 'umi';
import { useRequest } from 'ahooks';
import { Layout, Tabs, Icon } from 'tea-component';
import Loading from '@gp/components/Loading';
import PageAccess from '@gp/components/PageAccess';
import GeneralImageNew from '@gp/components/GeneralImageNew';
import TriggerControlBtn from '@gp/components/TriggerControlBtn';
import HeaderDetails from '@gp/components/HeaderDetails';
import FixedHeader from '@gp/components/HeaderDetails/FixedHeader';
import OverviewDetails from './Tabs/Overview/Details';
import CalendarDetails from './Tabs/Calendar/Details';
import ProjectsDetails from './Tabs/Projects/Details';
import FinanceDetails from './Tabs/Finance/Details';
import PeopleDetails from './Tabs/People/Details';
import ManagementDetails from './Tabs/Management/Details';
import DetailsHeaderCon from './components/DetailsHeaderCon';
import { queryPageAuthority } from '@gp/services/global';
import { queryDetailHeader } from './services/studioOverallDetail';
import { context as Context } from '@gp/components/Context';
import { getLocationIsValid } from '@gp/utils/common';
import { getMatchedTabId } from './utils';
import { SLASH_G_POP } from '@gp/constants/const';
import { DESCRIPTION_C } from '@gp/constants/noPermissionTips';

import type { ITab } from '@gp/types/global';

import './scss/details.scss';

const { Content } = Layout;

const TABS: ITab[] = [
  {
    id: 'overview',
    label: 'Overview',
    component: <OverviewDetails />,
  },
  {
    id: 'calendar',
    label: 'Calendar',
    component: <CalendarDetails />,
  },
  {
    id: 'projects',
    label: 'Projects',
    component: <ProjectsDetails />,
  },
  {
    id: 'finance',
    label: 'Finance',
    component: <FinanceDetails />,
  },
  {
    id: 'people',
    label: 'People',
    component: <PeopleDetails />,
  },
  {
    id: 'management_boardGovernance',
    label: 'Management',
    component: <ManagementDetails />,
  },
];

const StudioDetails: React.FC = () => {
  const filterObj: any = getLocationIsValid('/Studio/PortfolioOverview');
  const { studioId = '', studioName = '' } = filterObj || {};

  const { loading: pageAuthorityLoading, data: pageAuthorityData } = useRequest<any, any>(
    queryPageAuthority,
    {
      defaultParams: [{ assort: 'single_studio', id: studioId }],
    },
  );

  const { loading: headerLoading, data: headerData } = useRequest<any, any>(queryDetailHeader, {
    defaultParams: [{ studio_id: studioId }],
  });

  const scrollContentRef = useRef<HTMLDivElement>(null);
  const clickTab = useRef(true);

  const [activeId, setActiveId] = useState('');
  // 用于Calendar页签点击卡片跳转到Management页签默认展开对应会议
  const [meetingId, setMeetingId] = useState('');
  const [hideHeader, setHideHeader] = useState(false);

  // 滚动监听，头部隐藏或显示
  const handleScroll = useCallback(
    (e: any) => {
      const clientH = e?.target?.clientHeight;
      const scrollT = e?.target?.scrollTop;
      const scrollH = e?.target?.scrollHeight;
      if (scrollT === 0 && clickTab?.current) return; // 点击切换tab页签时不走下面的逻辑
      clickTab.current = false;
      /**
       * 滚动高度大于零，或者可视高度大于容器高度导致没有滚动条时隐藏头部
       * 滚动之后有可能头部消失导致可视高度大于容器高度，从而滚动条消失，然后头部又显示出来，这样就无法滚动看到下面的内容，此时隐藏头部处理
       */
      if (scrollT > 0 || clientH >= scrollH) {
        hideHeader || setHideHeader(true);
        return;
      }

      setHideHeader(false);
    },
    [hideHeader],
  );

  useEffect(() => {
    if (pageAuthorityLoading) return;
    const activeId = getMatchedTabId(TABS, filterObj);
    setActiveId(activeId);
  }, [pageAuthorityLoading, filterObj?.tab]);

  useEffect(() => {
    if (!activeId) return;
    if (scrollContentRef?.current) {
      scrollContentRef.current.scrollTop = 0;
    }
  }, [activeId]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (scrollContentRef?.current) {
        clearInterval(timer);
        scrollContentRef?.current?.addEventListener('scroll', handleScroll);
      }
    }, 500);
    return () => {
      setActiveId('');
      scrollContentRef?.current?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <Context.Provider
      value={{
        studioId,
        studioName,
        meetingId,
        setMeetingId,
        pageAuthorityData,
        headerLoading,
        headerData,
      }}
    >
      <Content className="dbg-game-details dbg-studio-details">
        <HeaderDetails
          title={hideHeader ? headerData?.name : 'Company'}
          className="games-bg dbg-studio-details-header"
          tabList={TABS}
          activeId={activeId}
          goBackHandle={() => {
            history.goBack();
          }}
          change={(tab: ITab | any) => {
            clickTab.current = true;
            const filter = { ...filterObj };
            filter.tab = tab?.id;
            // tab切换的时候，删除meetingId
            if (filter?.meetingId) {
              delete filter?.meetingId;
            }
            const newFilter = encodeURIComponent(JSON.stringify(filter));
            history.replace(`${SLASH_G_POP}/Studio/PortfolioOverview/Detail?filter=${newFilter}`);
            setActiveId(tab?.id);
          }}
          operation={
            !headerLoading &&
            !pageAuthorityLoading &&
            hideHeader && (
              <TriggerControlBtn
                isFold={hideHeader}
                onClick={() => {
                  setHideHeader(!hideHeader);
                }}
              />
            )
          }
        >
          {hideHeader ? (
            <div style={{ height: 96, color: '#262832' }}>
              <Loading />
            </div>
          ) : (
            <DetailsHeaderCon
              loading={headerLoading || pageAuthorityLoading}
              isHideHeader={hideHeader}
              data={headerData}
            />
          )}
        </HeaderDetails>

        {/* 头部吸顶结构 */}
        <Content.Body full>
          <FixedHeader>
            <div className="dbg-fixedheader-infobox">
              <Icon
                className="dbg-fixedheader-infobox-back"
                type="btnback"
                onClick={() => {
                  history.goBack();
                }}
              />
              <GeneralImageNew
                className="dbg-fixedheader-infobox-img"
                domain="game"
                width={24}
                height={24}
                src={headerData?.img_url}
              />
              <div className="dbg-fixedheader-gameinfo-name">{headerData?.name}</div>
            </div>

            <div className="dbg-fixedheader-tabs">
              <Tabs
                ceiling
                tabs={TABS}
                animated={false}
                activeId={activeId}
                onActive={(tab: ITab | any) => {
                  clickTab.current = true;
                  const filter = { ...filterObj };
                  filter.tab = tab?.id;
                  // tab切换的时候，删除meetingId
                  if (filter?.meetingId) {
                    delete filter?.meetingId;
                  }
                  const newFilter = encodeURIComponent(JSON.stringify(filter));
                  history.replace(
                    `${SLASH_G_POP}/Studio/PortfolioOverview/Detail?filter=${newFilter}`,
                  );
                  setActiveId(tab?.id);
                }}
              />
            </div>
          </FixedHeader>
          <>
            {pageAuthorityLoading ? (
              <div style={{ height: 'calc(100vh - 187px - 60px)', color: '#262832' }}>
                <Loading />
              </div>
            ) : (
              TABS.map((tab) => {
                const { id: tabId, component: tabComponent } = tab || {};
                if (tabId !== activeId) return null;
                return (
                  <PageAccess
                    className="dbg-studio-details__page-access"
                    key={tabId}
                    isAccess={!!pageAuthorityData?.[tabId]}
                    description={DESCRIPTION_C}
                  >
                    {tabComponent}
                  </PageAccess>
                );
              })
            )}
          </>
        </Content.Body>
      </Content>
    </Context.Provider>
  );
};

export default React.memo(StudioDetails);
